/**
 * A question is Draft or Published. There is no third state.
 *
 * **The bug this exists to kill.** The same fact was spelled five ways: the
 * editor said `draft | in_review | approved | published | retired`, the bank
 * said `Ready | Draft`, the library said `Saved | Draft`, and a paper said
 * `Ready`. Each surface mapped between them on its own, and one of those maps
 * compared against `"ready"`, a value the editor never writes, so a published
 * question read as Draft in its own history. Review and approval are not
 * states this product has.
 *
 * Every surface uses these two values and these labels. `toQuestionStatus` is
 * the only reader of anything older: it maps the legacy spellings, so stored
 * data and old fixtures keep working without every caller knowing them.
 */

import type { StatusBadgeTone } from "@exxatdesignux/ui/lib/status-badge-tints"

export const QUESTION_STATUSES = ["Published", "Draft"] as const

export type QuestionStatus = (typeof QUESTION_STATUSES)[number]

/** The editor's form value for the same two states. */
export type QuestionFormStatus = "draft" | "published"

export const QUESTION_STATUS_LABEL: Record<QuestionStatus, string> = {
  Published: "Published",
  Draft: "Draft",
}

export const QUESTION_STATUS_TONE: Record<QuestionStatus, StatusBadgeTone> = {
  Published: "success",
  Draft: "neutral",
}

export const QUESTION_STATUS_ICON: Record<QuestionStatus, string> = {
  Published: "fa-circle-check",
  Draft: "fa-pen",
}

export const QUESTION_STATUS_DESCRIPTION: Record<QuestionStatus, string> = {
  Published: "Live in the bank. Can go on an assessment.",
  Draft: "Still being written. Not eligible for use.",
}

/**
 * Any spelling of a status, as the one status it means.
 *
 * Only an explicit draft is a draft. A value that is missing, or one of the
 * retired spellings (Ready, Saved, in_review, approved, published, retired),
 * reads as Published, which is what the bank has always treated it as.
 */
export function toQuestionStatus(value: unknown): QuestionStatus {
  return typeof value === "string" && value.trim().toLowerCase() === "draft"
    ? "Draft"
    : "Published"
}

export function isPublished(value: unknown): boolean {
  return toQuestionStatus(value) === "Published"
}

export function toFormStatus(value: unknown): QuestionFormStatus {
  return toQuestionStatus(value) === "Draft" ? "draft" : "published"
}

export function fromFormStatus(value: unknown): QuestionStatus {
  return toQuestionStatus(value)
}
